import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMatches } from '../../../shared/hooks/useMatches';
import { useMobile } from '../../../shared/hooks/useMobile';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import StatsTab from './StatsTab';
import KeyEventsTab from './KeyEventsTab';
import LineupTab from './LineupTab';
import MatchDetailsTab from './MatchDetailsTab';
import MapsTab from './MapsTab';
import ReportsAppHeader from '../../../shared/components/layout/ReportsAppHeader';
import Button from '../../../shared/components/ui/Button';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';

const MatchDetailPage: React.FC = () => {
  const { matchId: fullMatchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useMobile();
  const [activeTab, setActiveTab] = useState<'details' | 'lineups' | 'stats' | 'keyevents' | 'tactics'>('details');
  
  // Extract organization ID from URL for proper navigation
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);
  
  // Extract actual matchId from the URL
  // Handle both old format (with -match-center) and new format (clean)
  const matchId = fullMatchId?.replace('-match-center', '') || '';
  
  // Debug logging to help troubleshoot
  console.log('MatchDetailPage Debug:', {
    fullMatchId,
    matchId,
    pathname: location.pathname,
    hash: location.hash
  });
  
  const { matches, loading: matchesLoading } = useMatches();
  const currentMatch = matches?.find(match => match.id === matchId);

  // Handle hash-based tab detection
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && ['details', 'lineups', 'stats', 'keyevents', 'tactics'].includes(hash)) {
      setActiveTab(hash as 'details' | 'lineups' | 'stats' | 'keyevents' | 'tactics');
    }
  }, [location.hash]);

  useEffect(() => {
    if (!matchId) {
      navigate('/reports');
      return;
    }
  }, [matchId, navigate]);

  if (matchesLoading) {
    return <LoadingSpinner />;
  }

  if (!currentMatch) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="text-red-600 mb-2">Match not found</div>
          <div className="text-sm text-gray-500">The requested match could not be found.</div>
        </div>
      </div>
    );
  }

  const handleTabChange = (tabId: 'details' | 'lineups' | 'stats' | 'keyevents' | 'tactics') => {
    setActiveTab(tabId);
    // Update URL hash without causing page reload
    window.history.replaceState(null, '', `${location.pathname}#${tabId}`);
  };

  const handleBack = () => {
    // Navigate back to reports match listing with proper organization context
    const targetPath = orgIdFromPath 
      ? `/o/${orgIdFromPath}/reports` 
      : '/reports';
    
    // Use window.location.href for reliable navigation
    // This ensures the page actually navigates even if React Router doesn't re-render
    // This is a workaround for the issue where React Router updates the URL but doesn't re-render
    window.location.href = targetPath;
  };

  const handleNavigateToReplay = () => {
    const targetMatchId = matchId || currentMatch.id;
    if (!targetMatchId) {
      console.warn('No match ID available for replay navigation');
      return;
    }

    const targetPath = orgIdFromPath
      ? `/o/${orgIdFromPath}/replay/${targetMatchId}`
      : `/replay/${targetMatchId}`;

    // Use full-page navigation to avoid the router stale state issue observed on the Tactics tab
    window.location.href = targetPath;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'details':
        return (
          <MatchDetailsTab 
            matchDetails={{
              id: currentMatch.id,
              home_team: {
                id: currentMatch.home_team.id,
                name: currentMatch.home_team.name,
                logo: currentMatch.home_team.logo || '',
                flag: '',
                city: (currentMatch.home_team.city as unknown as string) || '',
                venue: (currentMatch.home_team.venue as unknown as string) || '',
                date_established: (currentMatch.home_team.date_established as unknown as string) || '',
                website: (currentMatch.home_team.website as unknown as string) || '',
                social_media: (currentMatch.home_team.social_media as unknown as string) || ''
              },
              away_team: {
                id: currentMatch.away_team.id,
                name: currentMatch.away_team.name,
                logo: currentMatch.away_team.logo || '',
                flag: '',
                city: (currentMatch.away_team.city as unknown as string) || '',
                venue: (currentMatch.away_team.venue as unknown as string) || '',
                date_established: (currentMatch.away_team.date_established as unknown as string) || '',
                website: (currentMatch.away_team.website as unknown as string) || '',
                social_media: (currentMatch.away_team.social_media as unknown as string) || ''
              },
              home_score: currentMatch.home_score || 0,
              away_score: currentMatch.away_score || 0,
              name: currentMatch.name || '',
              start_date: currentMatch.start_date || '',
              end_date: currentMatch.end_date || null,
              venue: currentMatch.venue || '',
              home_team_id: currentMatch.home_team_id || '',
              away_team_id: currentMatch.away_team_id || '',
              tournament_id: currentMatch.tournament_id || '',
              status: (currentMatch.status as 'scheduled' | 'live' | 'finished') || 'scheduled',
              created_at: currentMatch.created_at || '',
              updated_at: currentMatch.updated_at || '',
              tournament: {
                id: (currentMatch.tournament?.id as string) || '',
                name: (currentMatch.tournament?.name as string) || '',
                logo: (currentMatch.tournament?.logo as string) || '',
                country: (currentMatch.tournament?.country as string) || ''
              },
              match_officials: []
            }}
          />
        );
      case 'lineups':
        return <LineupTab />;
      case 'stats':
        return <StatsTab 
          matchDetails={{
            id: currentMatch.id,
            home_team: {
              id: currentMatch.home_team.id,
              name: currentMatch.home_team.name,
              logo: currentMatch.home_team.logo || '',
              flag: '',
              city: (currentMatch.home_team.city as unknown as string) || '',
              venue: (currentMatch.home_team.venue as unknown as string) || '',
              date_established: (currentMatch.home_team.date_established as unknown as string) || '',
              website: (currentMatch.home_team.website as unknown as string) || '',
              social_media: (currentMatch.home_team.social_media as unknown as string) || ''
            },
            away_team: {
              id: currentMatch.away_team.id,
              name: currentMatch.away_team.name,
              logo: currentMatch.away_team.logo || '',
              flag: '',
              city: (currentMatch.away_team.city as unknown as string) || '',
              venue: (currentMatch.away_team.venue as unknown as string) || '',
              date_established: (currentMatch.away_team.date_established as unknown as string) || '',
              website: (currentMatch.away_team.website as unknown as string) || '',
              social_media: (currentMatch.away_team.social_media as unknown as string) || ''
            },
            home_score: currentMatch.home_score || 0,
            away_score: currentMatch.away_score || 0,
            name: currentMatch.name,
            start_date: currentMatch.start_date,
            end_date: currentMatch.end_date || null,
            venue: currentMatch.venue || '',
            home_team_id: currentMatch.home_team_id,
            away_team_id: currentMatch.away_team_id,
            tournament_id: currentMatch.tournament_id || '',
            status: (currentMatch.status as 'scheduled' | 'live' | 'finished') || 'scheduled',
            created_at: currentMatch.created_at,
            updated_at: currentMatch.updated_at,
            tournament: {
              id: (currentMatch.tournament?.id as string) || '',
              name: (currentMatch.tournament?.name as string) || '',
              logo: (currentMatch.tournament?.logo as string) || '',
              country: (currentMatch.tournament?.country as string) || ''
            },
            match_officials: []
          }}
        />;
      case 'keyevents':
        return <KeyEventsTab 
          matchDetails={{
            id: currentMatch.id,
            home_team: {
              id: currentMatch.home_team.id,
              name: currentMatch.home_team.name,
              logo: currentMatch.home_team.logo || '',
              flag: '',
              city: (currentMatch.home_team.city as unknown as string) || '',
              venue: (currentMatch.home_team.venue as unknown as string) || '',
              date_established: (currentMatch.home_team.date_established as unknown as string) || '',
              website: (currentMatch.home_team.website as unknown as string) || '',
              social_media: (currentMatch.home_team.social_media as unknown as string) || ''
            },
            away_team: {
              id: currentMatch.away_team.id,
              name: currentMatch.away_team.name,
              logo: currentMatch.away_team.logo || '',
              flag: '',
              city: (currentMatch.away_team.city as unknown as string) || '',
              venue: (currentMatch.away_team.venue as unknown as string) || '',
              date_established: (currentMatch.away_team.date_established as unknown as string) || '',
              website: (currentMatch.away_team.website as unknown as string) || '',
              social_media: (currentMatch.away_team.social_media as unknown as string) || ''
            },
            home_score: currentMatch.home_score || 0,
            away_score: currentMatch.away_score || 0,
            name: currentMatch.name,
            start_date: currentMatch.start_date,
            end_date: currentMatch.end_date || null,
            venue: currentMatch.venue || '',
            home_team_id: currentMatch.home_team_id,
            away_team_id: currentMatch.away_team_id,
            tournament_id: currentMatch.tournament_id || '',
            status: (currentMatch.status as 'scheduled' | 'live' | 'finished') || 'scheduled',
            created_at: currentMatch.created_at,
            updated_at: currentMatch.updated_at,
            tournament: {
              id: (currentMatch.tournament?.id as string) || '',
              name: (currentMatch.tournament?.name as string) || '',
              logo: (currentMatch.tournament?.logo as string) || '',
              country: (currentMatch.tournament?.country as string) || ''
            },
            match_officials: []
          }}
          isActive={activeTab === 'keyevents'}
        />;
      case 'tactics':
        return <MapsTab 
          matchId={matchId}
          homeTeamId={currentMatch.home_team_id}
          awayTeamId={currentMatch.away_team_id}
          homeTeamName={currentMatch.home_team.name}
          awayTeamName={currentMatch.away_team.name}
          onSaveButtonClick={() => {}}
        />;
      default:
        return (
          <MatchDetailsTab 
            matchDetails={{
              id: currentMatch.id,
              home_team: {
                id: currentMatch.home_team.id,
                name: currentMatch.home_team.name,
                logo: currentMatch.home_team.logo || '',
                flag: '',
                city: (currentMatch.home_team.city as unknown as string) || '',
                venue: (currentMatch.home_team.venue as unknown as string) || '',
                date_established: (currentMatch.home_team.date_established as unknown as string) || '',
                website: (currentMatch.home_team.website as unknown as string) || '',
                social_media: (currentMatch.home_team.social_media as unknown as string) || ''
              },
              away_team: {
                id: currentMatch.away_team.id,
                name: currentMatch.away_team.name,
                logo: currentMatch.away_team.logo || '',
                flag: '',
                city: (currentMatch.away_team.city as unknown as string) || '',
                venue: (currentMatch.away_team.venue as unknown as string) || '',
                date_established: (currentMatch.away_team.date_established as unknown as string) || '',
                website: (currentMatch.away_team.website as unknown as string) || '',
                social_media: (currentMatch.away_team.social_media as unknown as string) || ''
              },
              home_score: currentMatch.home_score || 0,
              away_score: currentMatch.away_score || 0,
              name: currentMatch.name || '',
              start_date: currentMatch.start_date || '',
              end_date: currentMatch.end_date || null,
              venue: currentMatch.venue || '',
              home_team_id: currentMatch.home_team_id || '',
              away_team_id: currentMatch.away_team_id || '',
              tournament_id: currentMatch.tournament_id || '',
              status: (currentMatch.status as 'scheduled' | 'live' | 'finished') || 'scheduled',
              created_at: currentMatch.created_at || '',
              updated_at: currentMatch.updated_at || '',
              tournament: {
                id: (currentMatch.tournament?.id as string) || '',
                name: (currentMatch.tournament?.name as string) || '',
                logo: (currentMatch.tournament?.logo as string) || '',
                country: (currentMatch.tournament?.country as string) || ''
              },
              match_officials: []
            }}
          />
        );
    }
  };

  // Define tabs
  const tabs = [
    { id: 'details', label: 'Details', icon: '📋' },
    { id: 'lineups', label: 'Lineups', icon: '👥' },
    { id: 'stats', label: 'Stats', icon: '📊' },
    { id: 'keyevents', label: 'Events', icon: '⚡' },
    { id: 'tactics', label: 'Tactics', icon: '🎯' }
  ];

  // Format match date
  const formatMatchDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Format match time
  const formatMatchTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Consistent Header */}
      <ReportsAppHeader
        isMobile={isMobile}
        // No onToggleSidebar prop - no sidebar on detail pages
        leftContent={
          // No back button on mobile - use native navigation
          !isMobile ? (
            <Button
              onClick={handleBack}
              variant="ghost"
              size="md"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              }
              iconPosition="left"
            >
              Back to Matches
            </Button>
          ) : null
        }
        centerContent={
          <div className={`flex ${isMobile ? 'space-x-6' : 'space-x-8'} ${isMobile ? 'justify-center' : ''}`}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id as any)}
                className={`py-2 px-2 border-b-2 font-medium ${isMobile ? 'text-sm' : 'text-sm'} flex items-center ${isMobile ? 'space-x-1' : 'space-x-2'} min-h-44 ${
                  activeTab === tab.id
                    ? 'border-gray-400 text-gray-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                title={isMobile ? tab.label : undefined}
              >
                <span className={isMobile ? 'text-lg' : ''}>{tab.icon}</span>
                {!isMobile && <span>{tab.label}</span>}
              </button>
            ))}
          </div>
        }
        rightContent={
          <div className="flex items-center space-x-2">
            {/* Watch Match Video Button (Secondary - Left) */}
            <Button
              onClick={handleNavigateToReplay}
              variant="secondary"
              size="md"
              className="min-w-[120px] transition-all duration-200"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m-6-8h8a2 2 0 012 2v8a2 2 0 01-2 2H8a2 2 0 01-2-2V6a2 2 0 012-2z" />
                </svg>
              }
            >
              {'Video'}
            </Button>
            
            {/* Save Configuration Button (for Tactics tab) or Insights Button (for other tabs) */}
            {activeTab === 'tactics' ? (
              <Button
                onClick={() => {
                  // Call the save handler exposed by MapsTab
                  const saveHandler = (window as any).__mapsTabSaveHandler;
                  if (saveHandler) {
                    saveHandler();
                  }
                }}
                variant="primary"
                size="md"
                className="min-w-[120px] transition-all duration-200"
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                }
              >
                {'Save'}
              </Button>
            ) : (
              <Button
                onClick={() => {}}
                variant="primary"
                size="md"
                className="min-w-[120px] transition-all duration-200"
                disabled={true}
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                }
              >
                {'Insights'}
              </Button>
            )}
          </div>
        }
      />
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        
        {/* Match Header - Exact same as Score App */}
        <div className="bg-gradient-to-r from-gray-100 via-gray-200 to-gray-300 rounded-xl p-4 mb-6 text-gray-800 relative overflow-hidden">
          {/* Match Status Overlay - Removed */}
          
          <div className="text-center space-y-3">
            {/* Row 1: Date, Time, Tournament */}
            <div className="flex justify-center items-center space-x-8 text-xs">
              <span className="font-medium">{formatMatchDate(currentMatch.start_date)}</span>
              <span className="font-medium">{formatMatchTime(currentMatch.start_date)}</span>
              <div className="flex items-center space-x-2">
                <img 
                  src={currentMatch.tournament?.logo || 'https://via.placeholder.com/16x16/cccccc/666666?text=🏆'} 
                  alt={`${currentMatch.tournament?.name || 'League'} logo`}
                  className="w-4 h-4 rounded object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/16x16/cccccc/666666?text=🏆';
                  }}
                />
                <span className="font-medium">{currentMatch.tournament?.name || 'League'}</span>
              </div>
            </div>
            
            {/* Row 2: Team A, Score, Team B */}
            <div className="flex justify-center items-center space-x-8">
              <div className="flex items-center space-x-3">
                <img 
                  src={currentMatch.home_team?.logo || 'https://via.placeholder.com/32x32/cccccc/666666?text=🏟️'} 
                  alt={`${currentMatch.home_team?.name || 'Home Team'} logo`}
                  className="w-8 h-8 rounded object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/32x32/cccccc/666666?text=🏟️';
                  }}
                />
                <span className="font-semibold text-lg">{currentMatch.home_team?.name || 'Home Team'}</span>
              </div>
              
              <div className="flex items-center space-x-4">
                <span className="text-3xl font-bold">{currentMatch.home_score || 0}</span>
                <span className="text-lg font-medium">VS</span>
                <span className="text-3xl font-bold">{currentMatch.away_score || 0}</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <span className="font-semibold text-lg">{currentMatch.away_team?.name || 'Away Team'}</span>
                <img 
                  src={currentMatch.away_team?.logo || 'https://via.placeholder.com/32x32/cccccc/666666?text=🏟️'} 
                  alt={`${currentMatch.away_team?.name || 'Away Team'} logo`}
                  className="w-8 h-8 rounded object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/32x32/cccccc/666666?text=🏟️';
                  }}
                />
              </div>
            </div>
            
            {/* Conditional Row: Either Penalty Shootout OR Venue/Match Number */}
            {currentMatch.home_penalty_score !== undefined && currentMatch.away_penalty_score !== undefined && 
             (currentMatch.home_penalty_score > 0 || currentMatch.away_penalty_score > 0) ? (
              /* Show Penalty Shootout Scores */
              <div className="flex items-center justify-center space-x-2 mt-2 mb-2">
                <span className="text-xs text-gray-500 font-medium">penalties</span>
                <span className="text-xs font-semibold text-gray-600">({currentMatch.home_penalty_score})</span>
                <span className="text-xs text-gray-500">-</span>
                <span className="text-xs font-semibold text-gray-600">({currentMatch.away_penalty_score})</span>
              </div>
            ) : (
              /* Show Venue and Match Number */
              <div className="flex justify-center items-center space-x-8 text-xs">
                <span className="font-medium">{currentMatch.venue || 'TBD'}</span>
                <span className="font-medium">Match #{currentMatch.name}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </div>
  );
};

export default MatchDetailPage;
